import React, { ReactNode, useEffect } from 'react';
import { st, classes } from './StatusIndicator.st.css';
import Tooltip from '../Tooltip';
import deprecationLog from '../utils/deprecationLog';
import {
  FormFieldSuccessFilled,
  FormFieldWarningFilled,
  FormFieldErrorFilled,
} from '@wix/wix-ui-icons-common/system';
import Loader from '../Loader';
import {
  dataHooks,
  STATUS,
  TOOLTIP_PLACEMENT,
} from './StatusIndicator.constants';
import {
  StatusIndicatorProps,
  StatusIndicatorState,
} from './StatusIndicator.types';

const statusToIconMap: Record<StatusIndicatorState, ReactNode> = {
  success: <FormFieldSuccessFilled />,
  warning: <FormFieldWarningFilled />,
  loading: <Loader size="tiny" />,
  error: <FormFieldErrorFilled />,
};

const StatusIndicator = (props: StatusIndicatorProps) => {
  const {
    dataHook,
    status = STATUS.ERROR,
    message,
    tooltipPlacement = TOOLTIP_PLACEMENT.TOP,
    className,
    tooltipProps,
  } = props;

  useEffect(() => {
    if ('tooltipPlacement' in props) {
      deprecationLog(
        '<StatusIndicator/> - prop "tooltipPlacement" is deprecated and will be removed in next major release, please use "tooltipProps" instead.',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const icon = statusToIconMap[status];

  const renderIcon = () => {
    if (message) {
      return (
        <span tabIndex={0} className={classes.iconWrapper}>
          {icon}
        </span>
      );
    }
    return icon;
  };

  return (
    <div
      className={st(classes.root, { status }, className)}
      data-hook={dataHook}
      data-status={status}
    >
      {message ? (
        <Tooltip
          dataHook={dataHooks.tooltip}
          appendTo="window"
          placement={tooltipPlacement}
          exitDelay={100}
          content={message}
          maxWidth={250}
          {...tooltipProps}
        >
          {renderIcon()}
        </Tooltip>
      ) : (
        icon
      )}
    </div>
  );
};

StatusIndicator.displayName = 'StatusIndicator';

export default StatusIndicator;
